/**
 * MediaCard - Reusable card component for displaying TV shows, movies, and podcasts
 * Used across Discover pages for consistent display
 */
import React from 'react';
import { Button } from './ui/button';
import { Tv, Film, Mic, Star, Plus, Eye, Headphones } from 'lucide-react';
import { FriendsBadge } from './FriendsBadge';

export function MediaCard({ 
  item, 
  mediaType = 'tv', // 'tv' | 'movie' | 'podcast'
  onClick, 
  onAddToList, 
  onMarkWatched,
  showActions = true,
  showFriendsBadge = true,
  testIdPrefix = 'media'
}) {
  // Determine badge style based on recommendation type
  const getBadge = () => {
    if (item.recommendation_type === 'similar') {
      return { bg: 'bg-blue-500', icon: '📺', label: 'Similar' };
    } else if (item.recommendation_type === 'genre') {
      return { bg: 'bg-emerald-500', icon: '🎭', label: 'Genre' };
    } else if (item.recommendation_type === 'person') {
      return { bg: 'bg-amber-500', icon: '⭐', label: 'Cast' };
    }
    return null;
  };
  
  const badge = getBadge();
  
  // Get the appropriate icon based on media type
  const MediaIcon = mediaType === 'podcast' ? Mic : mediaType === 'movie' ? Film : Tv;
  
  // Get the appropriate date field
  const dateField = mediaType === 'movie' ? 'release_date' : 'first_air_date';
  const dateValue = item[dateField] || item.first_air_date;
  
  // Get the appropriate title field
  const title = item.title || item.name;
  
  // Get image - handle both poster_path (TMDB) and image/thumbnail (podcasts)
  const imageSrc = item.poster_path || item.image || item.thumbnail;
  
  // ID field - TMDB uses tmdb_id, podcasts use podcast_id or id
  const itemId = item.tmdb_id || item.podcast_id || item.id;
  
  return (
    <div
      className="group cursor-pointer relative"
      data-testid={`${testIdPrefix}-card-${itemId}`}
    >
      <div 
        className="relative aspect-[2/3] rounded-lg overflow-hidden shadow-md group-hover:shadow-xl transition-shadow" 
        onClick={onClick}
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full bg-slate-200 flex items-center justify-center">
            <MediaIcon className="w-8 h-8 text-slate-400" />
          </div>
        )}
        
        {/* Rating Badge */}
        {item.vote_average > 0 && (
          <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
            {item.vote_average.toFixed(1)}
          </div>
        )}
        
        {/* Listen Score Badge (for podcasts) */}
        {item.listen_score > 0 && !item.vote_average && (
          <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
            {item.listen_score}
          </div>
        )}
        
        {/* Recommendation Type Badge */}
        {badge && (
          <div className={`absolute top-2 left-2 ${badge.bg} text-white text-xs px-2 py-1 rounded-full flex items-center gap-1`}>
            <span>{badge.icon}</span>
            {badge.label}
          </div>
        )}
        
        {/* Friends Badge - shows how many friends have this item */}
        {showFriendsBadge && !badge && (
          <FriendsBadge 
            itemId={String(itemId)} 
            mediaType={mediaType} 
            className="absolute top-2 left-2"
          />
        )}
        
        {/* Hover Actions */}
        {showActions && (
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); onAddToList?.(item); }}
              className="w-full bg-white text-[#3D4F6F] hover:bg-slate-100 rounded-full text-xs"
              data-testid={`add-to-list-${itemId}`}
            >
              <Plus className="w-3 h-3 mr-1" />
              Add to List
            </Button>
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); onMarkWatched?.(item); }}
              className="w-full bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full text-xs"
              data-testid={`mark-watched-${itemId}`}
            >
              {mediaType === 'podcast' ? (
                <Headphones className="w-3 h-3 mr-1" />
              ) : (
                <Eye className="w-3 h-3 mr-1" />
              )}
              {mediaType === 'podcast' ? 'Listened' : 'Watched'}
            </Button>
          </div>
        )}
      </div>
      
      {/* Title */}
      <h3 className="mt-2 text-sm font-medium text-[#3D4F6F] truncate">{title}</h3>
      
      {/* Year / Publisher */}
      {dateValue && (
        <p className="text-xs text-[#64748B]">
          {(() => { try { return new Date(dateValue).getFullYear(); } catch { return ''; } })()}
        </p>
      )}
      {item.publisher && !dateValue && (
        <p className="text-xs text-[#64748B] truncate">{item.publisher}</p>
      )}
      
      {/* Recommendation Reason */}
      {item.reason && (
        <p className="text-xs text-[#64748B] sm:line-clamp-2 mt-1 italic">{item.reason}</p>
      )}
    </div>
  );
}

export default MediaCard;
